import { CityMapRenderer, type MapScene } from '../render/cityMap';
import { type MapTheme } from '../render/theme';
import { readSafeArea, watchResize } from './stage';

/** Room for the badge above and below a node, and for the plate beside it. */
const PIN_MARGIN_Y = 24;
const PIN_MARGIN_X = 48;

export class MapPanel {
  private readonly frame: HTMLElement;
  private readonly scene: MapScene;
  private readonly theme: MapTheme;
  private renderer: CityMapRenderer | null = null;
  private stopWatching: () => void = () => {};

  constructor(frame: HTMLElement, scene: MapScene, theme: MapTheme) {
    this.frame = frame;
    this.scene = scene;
    this.theme = theme;
  }

  mount(): void {
    const canvas = this.frame.querySelector<HTMLCanvasElement>('canvas');
    if (!canvas) return;
    this.renderer = new CityMapRenderer(canvas, PIN_MARGIN_X, () => this.layout());
    this.layout();
    this.stopWatching = watchResize(() => this.layout());
  }

  unmount(): void {
    this.stopWatching();
    this.renderer?.dispose();
    this.renderer = null;
  }

  layout(): void {
    const renderer = this.renderer;
    if (!renderer) return;
    const safe = readSafeArea(this.frame);
    renderer.setInsets({
      top: safe.top + PIN_MARGIN_Y,
      right: safe.right + PIN_MARGIN_X,
      bottom: safe.bottom + PIN_MARGIN_Y,
      left: safe.left + PIN_MARGIN_X,
    });
    renderer.resize();
    renderer.render(this.scene, this.theme);

    const { width, height } = renderer.size;
    if (!(width > 0 && height > 0)) return;
    for (const node of this.scene.graph.nodes) {
      const pin = this.frame.querySelector<HTMLElement>(`[data-pin="${node.id}"]`);
      if (!pin) continue;
      const p = renderer.project(node.position);
      pin.style.left = `${(p.x / width) * 100}%`;
      pin.style.top = `${(p.y / height) * 100}%`;
    }
  }
}
